import React, { Fragment, useState, useEffect } from 'react';
import {
    Box,
    Button,
    Input,
    Radio,
    RadioGroup,
    Stack,
    Select,
    Spinner,
    Text,
    Flex,
} from '@chakra-ui/react';
import axiosInstance from '../../Services/axiosInstance';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { FaSyncAlt } from 'react-icons/fa';
import { useSelector } from 'react-redux';

const ExportCustomAudience = ({
    formData,
    selectedElement,
    selectTemplate,
    segmentId,

    selectedAdAccount,
    setSelectedAdAccount,

    customAudienceList,
    setCustomAudienceList,

    hasAdAccount,
    setHasAdAccount,

    hasCustomAudience,
    setHasCustomAudience,

    isFbConnected,
    setIsFbConnected,

    adAccountsList,
    setAdAccountsList,

    customAudience,
    setCustomAudience
}) => {

    const [loading, setLoading] = useState(false);
    const [checkingMeta, setCheckingMeta] = useState(true);

    const userDetails = useSelector(state => state.auth.user_data);

    // ✅ FETCH APIs
    const getAdsAccountsList = () => {
        let url = `${process.env.REACT_APP_API_ENDPOINT}/zc/zads/account/list/adv`;
        return axiosInstance.get(url);
    };

    const checkFbAuth = () => {
        let url = `${process.env.REACT_APP_API_ENDPOINT}/fb/fbmessenger/credentials?restful=true&channel=facebook`;
        return axiosInstance.get(url);
    };

    const getCustomAudience = (accountId) => {
        let url = `${process.env.REACT_APP_API_ENDPOINT}/analytics/rest/v1/customaudience/accounts/${accountId}/customaudiences`;
        return axiosInstance.get(url);
    };

    // ✅ INIT LOAD
    useEffect(() => {
        checkFbAuth()
            .then((res) => setIsFbConnected(res.data.loggedIn))
            .finally(() => setCheckingMeta(false));

        getAdsAccountsList().then(response => {
            const list = response?.data?.data?.data || [];
            setAdAccountsList(list);
        });
    }, []);

    // ✅ SYNC SELECTED ACCOUNT AFTER LIST LOAD
    useEffect(() => {
        if (selectedAdAccount && adAccountsList?.length) {
            const exists = adAccountsList.find(
                acc => acc.account_id === selectedAdAccount
            );
            if (exists) {
                setHasAdAccount(true);
            }
        }
    }, [adAccountsList, selectedAdAccount]);

    // ✅ FETCH AUDIENCE WHEN ACCOUNT CHANGES
    useEffect(() => {
        if (selectedAdAccount) {
            fetchAudienceList(selectedAdAccount);
        }
    }, [selectedAdAccount]);

    const fetchAudienceList = (accountId) => {
        setLoading(true);

        getCustomAudience(accountId)
            .then((response) => {
                const audiences = response?.data?.data?.data || [];
                setCustomAudienceList(audiences);
                setHasCustomAudience(audiences.length > 0);

                if (audiences.length > 0 && !customAudience?.create) {
                    handleAudienceSelect(audiences[0].id);
                }
            })
            .catch(() => {
                setCustomAudienceList([]);
                setHasCustomAudience(false);
            })
            .finally(() => setLoading(false));
    };

    // ✅ SELECT ACCOUNT + SAVE TO formData
    const selectAdAccount = (accountId) => {
        setSelectedAdAccount(accountId);
        setHasAdAccount(true);

        // Update formData through template
        setTimeout(() => {
            updateCreateCustomAudience(customAudience?.selected?.id);
        }, 50);
    };

    // ✅ SELECT AUDIENCE
    const handleAudienceSelect = (e) => {
        const selectedId = e?.target?.value || e;
        const selectedAud = customAudienceList.find(aud => aud.id === selectedId);

        if (selectedAud) {
            setCustomAudience(prev => ({
                ...prev,
                selected: selectedAud
            }));

            updateCreateCustomAudience(selectedId);
        }
    };

    // ✅ FIXED RADIO CHANGE - Using immediate value
    const handleRadioChange = (value) => {
        const isCreateNew = value === 'create-new';

        setCustomAudience(prev => ({
            ...prev,
            create: isCreateNew,
            selected: isCreateNew ? null : prev.selected
        }));

        // Use the new value directly (avoid stale closure)
        setTimeout(() => {
            if (isCreateNew) {
                createNewCustomAudience(
                    customAudience?.newCustomAudienceData?.name || '',
                    customAudience?.newCustomAudienceData?.description || ''
                );
            } else {
                const currentId = customAudience?.selected?.id;
                if (currentId) {
                    updateCreateCustomAudience(currentId);
                }
            }
        }, 100);
    };

    // ✅ TEMPLATE BUILDERS
    const createNewCustomAudience = (name, desc) => {
        let data = {
            newCustomAudienceData: {
                isNew: true,
                name: name || "",
                description: desc || "",
                selectedAdAccount,
                user_id: userDetails.id
            },
            type: "custom",
            segmentId,
            dates: {
                startDate: moment().subtract(30, "days"),
                endDate: moment()
            }
        };

        selectTemplate(btoa(JSON.stringify(data)));
    };

    const updateCustomAudience = (name, desc, id) => {
        let data = {
            newCustomAudienceData: {
                isNew: false,
                name,
                description: desc || "",
                selectedAdAccount,
                customAudienceId: id,
                user_id: userDetails.id
            },
            type: "custom",
            segmentId,
            dates: {
                startDate: moment().subtract(30, "days"),
                endDate: moment()
            }
        };

        selectTemplate(btoa(JSON.stringify(data)));
    };

    const updateCreateCustomAudience = (id) => {
        if (!customAudience.create && id) {
            const aud = customAudienceList.find(a => a.id === id);
            if (aud) {
                updateCustomAudience(aud.name, aud.description, aud.id);
            }
        } else if (customAudience.create) {
            createNewCustomAudience(
                customAudience?.newCustomAudienceData?.name,
                customAudience?.newCustomAudienceData?.description
            );
        }
    };

    // ✅ RESTORE VALUES WHEN EDITING FLOW
    useEffect(() => {
        if (selectedElement?.id && formData[selectedElement?.id]?.action?.templateUUID) {
            try {
                const decoded = JSON.parse(atob(formData[selectedElement.id].action.templateUUID));
                const nd = decoded.newCustomAudienceData;

                if (nd) {
                    setSelectedAdAccount(nd.selectedAdAccount || '');
                    setHasAdAccount(!!nd.selectedAdAccount);

                    setCustomAudience({
                        create: nd.isNew,
                        selected: nd.isNew ? null : { id: nd.customAudienceId },
                        newCustomAudienceData: {
                            name: nd.name || '',
                            description: nd.description || ''
                        }
                    });
                }
            } catch (e) {
                console.error("Failed to decode templateUUID", e);
            }
        }
    }, [formData, selectedElement?.id]);

    return (
        <Fragment>
            <Box p="20px">
                <Flex direction="column" gap={4}>

                    {/* META CHECK */}
                    {checkingMeta ? (
                        <Spinner />
                    ) : !isFbConnected ? (
                        <Flex gap={2}>
                            <Link to={'/fb/auth/login/settings'} target="_blank">
                                Login with Meta
                            </Link>
                            <Button onClick={() => window.location.reload()}>
                                Refresh <FaSyncAlt />
                            </Button>
                        </Flex>
                    ) : (
                        <>
                            <Select
                                placeholder="Select Ad Account"
                                value={selectedAdAccount || ''}
                                onChange={(e) => selectAdAccount(e.target.value)}
                            >
                                {adAccountsList?.map(acc => (
                                    <option key={acc.account_id} value={acc.account_id}>
                                        {acc.name} | {acc.account_id}
                                    </option>
                                ))}
                            </Select>
                        </>
                    )}

                    {loading && <Spinner />}

                    {/* AUDIENCE SECTION */}
                    {!!hasAdAccount && !loading && (
                        <RadioGroup
                            value={customAudience.create ? 'create-new' : 'update-existing'}
                            onChange={handleRadioChange}
                        >
                            <Stack>
                                <Radio value="update-existing">
                                    Update Existing
                                </Radio>

                                {!customAudience.create && (
                                    <Select
                                        value={customAudience?.selected?.id || ''}
                                        onChange={handleAudienceSelect}
                                    >
                                        {customAudienceList.map(aud => (
                                            <option key={aud.id} value={aud.id}>
                                                {aud.name}
                                            </option>
                                        ))}
                                    </Select>
                                )}

                                <Radio value="create-new">
                                    Create New
                                </Radio>

                                {customAudience.create && (
                                    <>
                                        <Input
                                            placeholder="Name"
                                            value={customAudience?.newCustomAudienceData?.name || ''}
                                            onChange={(e) => {
                                                setCustomAudience(prev => ({
                                                    ...prev,
                                                    newCustomAudienceData: {
                                                        ...prev.newCustomAudienceData,
                                                        name: e.target.value
                                                    }
                                                }));
                                                setTimeout(() => createNewCustomAudience(e.target.value, customAudience?.newCustomAudienceData?.description), 150);
                                            }}
                                        />

                                        <Input
                                            placeholder="Description"
                                            value={customAudience?.newCustomAudienceData?.description || ''}
                                            onChange={(e) => {
                                                setCustomAudience(prev => ({
                                                    ...prev,
                                                    newCustomAudienceData: {
                                                        ...prev.newCustomAudienceData,
                                                        description: e.target.value
                                                    }
                                                }));
                                                setTimeout(() => createNewCustomAudience(customAudience?.newCustomAudienceData?.name, e.target.value), 150);
                                            }}
                                        />
                                    </>
                                )}
                            </Stack>
                        </RadioGroup>
                    )}
                </Flex>
            </Box>
        </Fragment>
    );
};

export default ExportCustomAudience;